
import { distinct, mergeMapTo, mergeMap, filter, map, mapTo, catchError } from 'rxjs/operators'
import { Redemption } from 'interfaces/bounty'
import { RewardsHistoryItemWithId } from 'components/features/rewards/rewardPage/store'
import { Epic } from 'interfaces/middleware'
import {
  GET_REDEMPTION_ACCOUNT,
  GET_REDEMPTIONS,
  GET_REWARDS,
  GET_REWARDS_SUMMARY,
  getRedemptionAccountActionCreator,
  getRedemptionsActionCreator,
  getRewardsActionCreator,
  getRewardSummaryActionCreator,
  setRedemptionStepNumberCreator,
  TOGGLE_DISMISS_REWARD,
  toggleBountyBlockLoadingActionCreator,
  toggleDismissRewardActionCreator,
  GET_REFERRAL_INFO,
  getReferralInfoActionCreator,
} from 'actions/bountyActions'
import { Observable, from, of, concat, empty } from 'rxjs'
import { get, post } from 'helpers/observableAjaxHelpers'
import { combineEpics } from 'redux-observable'
import { NOT_FOUND_STATUS_CODE } from 'constants/ajax'
import { Action } from 'actions'
import { processError } from 'helpers/ajaxHelpers'
import {
  GET_REDEMPTION_ACCOUNT_URL,
  GET_REDEMPTIONS_URL,
  GET_REWARDS_SUMMARY_URL,
  GET_REWARDS_URL,
  PATCH_REWARD_URL,
  GET_REFERRAL_INFO_URL,
} from 'interfaces/ajax'
import { Paginatable } from 'interfaces/table'
import { normalize } from 'helpers/normalizeHelpers'
import { constructGetRedemptionsApiPayload, constructGetRewardsApiPayload } from 'helpers/bountyHelpers'
import { PAGE_CHANGED, setUnexpectedErrorActionCreator } from 'actions/globalActions'
import { FORM_REDEMPTION } from 'constants/forms'
import { destroy } from 'redux-form'
import { REDEMPTION_ENTRY_URL } from 'tangle-shared'
import { FORM_STOP_SUBMIT, FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { getErrorType } from 'helpers/errorHelpers'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { REDEMPTION_PAGE } from 'constants/pages'
import { UrlWithId } from 'interfaces/ajax/base'
import { rewardItemsDataSelector } from 'selectors/bountySelectors'
import { REWARDS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { catchAjaxSafe } from 'rxOperators/catchSafe'
import { normalizeRewardItem } from 'components/features/rewards/rewardPage/helpers'

const getRewardsEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(GET_REWARDS, 'request')).pipe(
      mergeMap(({ payload }) => {
        const state = store.getState()
        const request$ = get(GET_REWARDS_URL, constructGetRewardsApiPayload(state, payload.params)).pipe(
          mergeMap(({ response }) => {
            const { results } = response

            const resultMapped = results.map(normalizeRewardItem)

            const successPayload: Paginatable<RewardsHistoryItemWithId, 'uniqKey'> = {
              ...response,
              results: normalize(resultMapped, 'uniqKey'),
            }

            return from([
              getRewardsActionCreator.success(payload.params, successPayload),
              toggleBountyBlockLoadingActionCreator('rewards-details', false),
            ])
          }),
          catchError((error): Observable<Action> => {
            if (error.status === NOT_FOUND_STATUS_CODE && payload.params.page !== 1) {
              return of(
                getRewardsActionCreator.request({ ...payload.params, page: 1 }),
              )
            }

            const toggleLoading$ = of(
              toggleBountyBlockLoadingActionCreator('rewards-details', false),
            )

            return concat(
              toggleLoading$,
              processError(error),
            )
          }))

        return concat(
          of(toggleBountyBlockLoadingActionCreator('rewards-details', true)),
          request$,
        )
      }))

const getRedemptionAccountEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_REDEMPTION_ACCOUNT, 'request')).pipe(
      mergeMap((action) =>
        get(GET_REDEMPTION_ACCOUNT_URL).pipe(
          map(({ response }) =>
            getRedemptionAccountActionCreator.success(action.payload.params, response),
          ))
          .pipe(catchAjaxSafe((error) => {
            return of(setUnexpectedErrorActionCreator(getErrorType(error), error))
          })),
      ))

const getRewardSummaryEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_REWARDS_SUMMARY, 'request')).pipe(
      distinct(
        () => true,
        action$.pipe(toDiscardCache(REWARDS_DISCARD_TRIGGERS)),
      ),
      mergeMap((action) =>
        get(GET_REWARDS_SUMMARY_URL).pipe(
          map(({ response }) =>
            getRewardSummaryActionCreator.success(action.payload.params, response),
          ))
          .pipe(catchAjaxSafe((error) => {
          // TODO: Try to handle error better
            return of(setUnexpectedErrorActionCreator(getErrorType(error), error))
          })),
      ))

const createRedemptionEpic: Epic = (action$) =>
  action$
    .pipe(byType(FORM_SUBMITTED_SUCCESSFULLY)).pipe(
      mergeMap(({ payload }) => {
        if (payload.formName !== FORM_REDEMPTION) {
          return empty()
        }

        return of(setRedemptionStepNumberCreator(3))
      }))

const resetRedemptionStepNumberEpic: Epic = (action$) =>
  action$
    .pipe(byType(PAGE_CHANGED)).pipe(
      filter(({ payload }) => !payload.next.startsWith(REDEMPTION_ENTRY_URL)),
      mergeMapTo(from([
        destroy(FORM_REDEMPTION),
        setRedemptionStepNumberCreator(null),
      ])))

const resetRedemptionFormScrollEpic: Epic = (action$) =>
  action$
    .pipe(byType(PAGE_CHANGED)).pipe(
      filter(({ payload }) => payload.next.startsWith(REDEMPTION_ENTRY_URL)),
      mapTo(activateAnchorActionCreator(REDEMPTION_PAGE)))

const reloadRewardBalanceEpic: Epic = (action$) =>
  action$
    .pipe(byType(FORM_STOP_SUBMIT)).pipe(
      filter((action) => action.meta.form === FORM_REDEMPTION),
      mapTo(getRedemptionAccountActionCreator.request({})))

// TODO: this epic is very similar to a `getRewardsEpic` but I need more time to create an entity-agnostic mechanism
const getRedemptionsEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(GET_REDEMPTIONS, 'request')).pipe(
      mergeMap(({ payload }) => {
        const state = store.getState()
        const request$ = get(GET_REDEMPTIONS_URL, constructGetRedemptionsApiPayload(state, payload.params)).pipe(
          mergeMap(({ response }) => {
            const setPayload: Paginatable<Redemption, 'id'> = {
              ...response,
              results: normalize(response.results, 'id'),
            }

            return from([
              getRedemptionsActionCreator.success(payload.params, setPayload),
              toggleBountyBlockLoadingActionCreator('redemptions', false),
            ])
          }),
          catchError((error): Observable<Action> => {
            if (error.status === NOT_FOUND_STATUS_CODE && payload.params.page !== 1) {
              return of(
                getRedemptionsActionCreator.request({ ...payload.params, page: 1 }),
              )
            }

            const toggleLoading$ = of(
              toggleBountyBlockLoadingActionCreator('redemptions', false),
            )

            return concat(
              toggleLoading$,
              processError(error),
            )
          }))

        return concat(
          of(toggleBountyBlockLoadingActionCreator('redemptions', true)),
          request$,
        )
      }))


const toggleDismissRewardEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_DISMISS_REWARD, 'request')).pipe(
      mergeMap((action) => {
        const { params: reward } = action.payload
        const { id, is_dismissed } = reward
        const apiPayload = { is_dismissed: !is_dismissed }
        const url: UrlWithId<typeof PATCH_REWARD_URL> = { url: PATCH_REWARD_URL, params: { id }}
        const state = store.getState()
        const items = rewardItemsDataSelector(state)
        const { page: statePage } = state.app.bounty.reward.items
        let page = statePage

        if (items && items.results.allKeys.length === 1) {
          page = statePage === 1 ? 1 : statePage - 1
        }

        const request$ = post(url, { body: apiPayload }).pipe(
          mergeMap(({ response }) => {
            return from([
              toggleDismissRewardActionCreator.success(reward, response),
              getRewardsActionCreator.request({ page }),
            ])
          }))
          .pipe(catchAjaxSafe(() =>
            of(
              toggleDismissRewardActionCreator.fail(reward),
            ),
          ))

        return request$
      }))

const getReferralInfoEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_REFERRAL_INFO, 'request')).pipe(
      mergeMap((action) =>
        get(GET_REFERRAL_INFO_URL).pipe(
          map(({ response }) =>
            getReferralInfoActionCreator.success(action.payload.params, response),
          ))
          .pipe(catchAjaxSafe((error) => {
            return of(setUnexpectedErrorActionCreator(getErrorType(error), error))
          }))
      ))


export default combineEpics(
  getRewardsEpic,

  getRedemptionAccountEpic,
  getRewardSummaryEpic,
  createRedemptionEpic,
  resetRedemptionStepNumberEpic,
  resetRedemptionFormScrollEpic,
  reloadRewardBalanceEpic,
  toggleDismissRewardEpic,

  getRedemptionsEpic,

  getReferralInfoEpic,
)
